import Phaser from 'phaser';
import BattleScene from 'tower/scenes/battle';
import { Direction, directionalVelocity, gridMove, wait } from 'tower/util';
import Room from '../room';
import * as C from 'tower/constants';
import gameState from 'tower/state/game-state';

const MOVE_SPEED = 200;

export interface ProjectileVariant {
  frame: number;
  animation?: string;
  bodyWidth: number;
  bodyHeight: number;
  rotate?: boolean;
  damage: number;
  destroyWalls?: boolean;
  hitWalls?: boolean;
  sparkOnDestroy?: boolean;
}

const VARIANTS: Record<string, ProjectileVariant> = {
  magic: {
    frame: 1,
    animation: 'magic',
    bodyWidth: 12,
    bodyHeight: 12,
    rotate: true,
    damage: 1,
  },
  wallMagic: {
    frame: 1,
    animation: 'magic',
    bodyWidth: 12,
    bodyHeight: 12,
    rotate: true,
    damage: 1,
    destroyWalls: true,
  },
  fireball: {
    frame: 7,
    get animation() {
      return gameState.getFlag('disableFlashing') ? undefined : 'fireball';
    },
    bodyWidth: 6,
    bodyHeight: 8,
    rotate: false,
    damage: 1,
  },
  arrow: {
    frame: 9,
    bodyWidth: 4,
    bodyHeight: 12,
    rotate: true,
    damage: 1,
    sparkOnDestroy: true,
  },
  swordBeam: {
    frame: 11,
    animation: 'swordBeam',
    bodyWidth: 6,
    bodyHeight: 12,
    rotate: true,
    damage: 2,
  },
};

export type ProjectileKey = keyof typeof VARIANTS;

interface ProjectileOptions {
  target?: 'enemy' | 'player';
}

export default class Projectile extends Phaser.GameObjects.Sprite {
  declare body: Phaser.Physics.Arcade.Body;
  _direction!: Direction;

  room: Room;
  public readonly damage: number;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantKey: keyof typeof VARIANTS,
    direction: Direction,
    public options: ProjectileOptions = {},
  ) {
    const variant = VARIANTS[variantKey];
    super(scene, x, y, 'projectiles', variant.frame);

    this.damage = variant.damage;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.body.setSize(variant.bodyWidth, variant.bodyHeight).setVelocity(...directionalVelocity(MOVE_SPEED, direction));
    this.setDepth(C.DEPTHS.Projectiles);

    // Set direction after body size in case it gets modified by rotation
    this.direction = direction;

    this.room = scene.currentRoom;
    this.room.projectiles.add(this);

    if (variant.animation && !gameState.getFlag('disableFlashing')) {
      this.play(variant.animation);
    }
  }

  get variant() {
    return VARIANTS[this.variantKey];
  }

  get direction() {
    return this._direction;
  }

  set direction(value: Direction) {
    this._direction = value;
    if (this.variant.rotate) {
      switch (value) {
        case Direction.Up:
          this.setAngle(0);
          this.body.setSize(this.variant.bodyWidth, this.variant.bodyHeight);
          return;
        case Direction.Right:
          this.setAngle(90);
          this.body.setSize(this.variant.bodyHeight, this.variant.bodyWidth);
          return;
        case Direction.Down:
          this.setAngle(180);
          this.body.setSize(this.variant.bodyWidth, this.variant.bodyHeight);
          return;
        case Direction.Left:
          this.setAngle(-90);
          this.body.setSize(this.variant.bodyHeight, this.variant.bodyWidth);
          return;
      }
    }
  }

  handleHitWall() {
    if (!this.isDestroyed) {
      if (this.variant.destroyWalls) {
        // Lazy: Rather than identify the wall by proper collision, get the nodes for last frame's position and use our
        // direction to find the wall to destroy.
        const nodes = this.room.maze.getNodesAtWorldXY(this.body.prev.x, this.body.prev.y);
        let destroyed = false;
        switch (this.direction) {
          case Direction.Up:
            destroyed = this.room.maze.removeWall(nodes[0], Direction.Right);
            break;
          case Direction.Right:
            destroyed = this.room.maze.removeWall(nodes[1], Direction.Down);
            break;
          case Direction.Down:
            destroyed = this.room.maze.removeWall(nodes[2], Direction.Right);
            break;
          case Direction.Left:
            destroyed = this.room.maze.removeWall(nodes[0], Direction.Down);
            break;
        }
        if (destroyed) {
          this.destroy();
        }
      } else if (this.variant.hitWalls) {
        this.destroy(true);
      }
    }
  }

  update() {
    if (!Phaser.Geom.Rectangle.Contains(this.room.wallRect, this.x, this.y)) {
      this.destroy(true);
    }
  }

  destroy(fromScene?: boolean) {
    const { scene, room } = this;

    if (this.variant.sparkOnDestroy) {
      const spark = scene.add.image(...gridMove(this.x, this.y, this.direction, 8), 'projectiles', 10);
      spark.setDepth(C.DEPTHS.Projectiles);
      wait(scene, 70).then(() => spark.destroy(true));
      room.misc.add(spark);
    }

    super.destroy(fromScene);
  }

  static toTarget(
    scene: BattleScene,
    x: number,
    y: number,
    variantKey: keyof typeof VARIANTS,
    direction: Direction,
    target: Phaser.Types.Math.Vector2Like,
    speed: number,
    options: ProjectileOptions = {},
  ) {
    const projectile = new Projectile(scene, x, y, variantKey, direction, options);
    scene.physics.moveToObject(projectile, target, speed);
  }

  static preload(scene: Phaser.Scene) {
    scene.load.spritesheet('projectiles', 'img/projectiles.png', { frameWidth: 16, frameHeight: 16 });
    scene.load.audio('magic', 'audio/magic.mp3');
  }

  static create(scene: Phaser.Scene) {
    scene.anims.create({
      key: `magic`,
      frameRate: 16,
      frames: scene.anims.generateFrameNumbers('projectiles', {
        frames: [1, 2, 3, 4],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `fireball`,
      frameRate: 32,
      frames: scene.anims.generateFrameNumbers('projectiles', {
        frames: [5, 6, 7, 8],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `swordBeam`,
      frameRate: 16,
      frames: scene.anims.generateFrameNumbers('projectiles', {
        frames: [11, 12, 13, 14],
      }),
      repeat: -1,
    });
  }
}
